import React, { useState, useRef, useEffect } from 'react';

const OBJECT_TYPES = new Set(['image', 'rich_text', 'shape']);
export const isObjectVisual = (vizType) => OBJECT_TYPES.has(vizType);

export function FreeVisual({ children, position, zIndex, isActive, onSelect, onPositionChange, onContextMenu, isEditable = true }) {
  const pos = position || { x: 100, y: 100, w: 200, h: 150 };
  const [dragOffset, setDragOffset] = useState(null);
  const [resizeOffset, setResizeOffset] = useState(null);
  const cleanupRef = useRef(null);

  const currentX = dragOffset ? Math.max(0, pos.x + dragOffset.dx) : pos.x;
  const currentY = dragOffset ? Math.max(0, pos.y + dragOffset.dy) : pos.y;
  const currentW = resizeOffset ? Math.max(16, pos.w + resizeOffset.dw) : pos.w;
  const currentH = resizeOffset ? Math.max(12, pos.h + resizeOffset.dh) : pos.h;

  useEffect(() => {
    return () => { if (cleanupRef.current) cleanupRef.current(); };
  }, []);

  const handleDragStart = (e) => {
    if (!isEditable || e.button !== 0) return;
    e.stopPropagation();
    const startMX = e.clientX;
    const startMY = e.clientY;
    setDragOffset({ dx: 0, dy: 0 });

    const handleMove = (ev) => {
      setDragOffset({ dx: ev.clientX - startMX, dy: ev.clientY - startMY });
    };
    const handleUp = (ev) => {
      const dx = ev.clientX - startMX;
      const dy = ev.clientY - startMY;
      setDragOffset(null);
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        onPositionChange({ ...pos, x: Math.max(0, pos.x + dx), y: Math.max(0, pos.y + dy) });
      }
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      cleanupRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    cleanupRef.current = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  };

  const handleResizeStart = (e) => {
    if (!isEditable || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const startMX = e.clientX;
    const startMY = e.clientY;
    setResizeOffset({ dw: 0, dh: 0 });

    const handleMove = (ev) => {
      setResizeOffset({ dw: ev.clientX - startMX, dh: ev.clientY - startMY });
    };
    const handleUp = (ev) => {
      const dw = ev.clientX - startMX;
      const dh = ev.clientY - startMY;
      setResizeOffset(null);
      onPositionChange({ ...pos, w: Math.max(16, pos.w + dw), h: Math.max(12, pos.h + dh) });
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      cleanupRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    cleanupRef.current = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  };

  return (
    <div
      data-testid={`free-visual-${isActive ? 'active' : 'idle'}`}
      className={`absolute group/free ${isActive ? 'ring-[5px] ring-primary ring-offset-2 ring-offset-background rounded-xl shadow-lg' : ''} ${dragOffset ? 'opacity-80' : ''}`}
      style={{
        left: currentX, top: currentY, width: currentW, height: currentH,
        zIndex: zIndex || 10,
        userSelect: dragOffset || resizeOffset ? 'none' : 'auto',
        transition: dragOffset || resizeOffset ? 'none' : 'box-shadow 0.15s',
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu(e); }}
    >
      {/* Drag handle bar */}
      {isEditable && (
        <div
          data-testid="free-visual-drag-handle"
          className="absolute top-0 left-0 right-0 h-6 cursor-grab active:cursor-grabbing flex items-center justify-center opacity-0 group-hover/free:opacity-100 transition-opacity z-10 rounded-t-xl"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 100%)' }}
          onMouseDown={handleDragStart}
        >
          <div className="flex gap-0.5">
            <div className="w-1 h-1 rounded-full bg-white/60" />
            <div className="w-1 h-1 rounded-full bg-white/60" />
            <div className="w-1 h-1 rounded-full bg-white/60" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="w-full h-full overflow-hidden">{children}</div>

      {/* Resize handle - bottom-right */}
      {isEditable && (
        <div
          data-testid="free-visual-resize-handle"
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-0 group-hover/free:opacity-100 transition-opacity z-10"
          onMouseDown={handleResizeStart}
        >
          <svg viewBox="0 0 12 12" className="w-3 h-3 absolute bottom-0.5 right-0.5">
            <path d="M11 1L1 11M11 5L5 11M11 9L9 11" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      )}

      {/* Selection corner dots */}
      {isActive && isEditable && (
        <>
          <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-primary rounded-full border border-background" />
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-primary rounded-full border border-background" />
          <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-primary rounded-full border border-background" />
          <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-primary rounded-full border border-background" />
        </>
      )}
    </div>
  );
}
